/**
 * The shell's icons, drawn rather than typed.
 *
 * The predecessor used text glyphs (⟳ ⓘ ⋯ ▲ ●) which the shipped IBM Plex
 * subset does not contain — the browser fell back to a different family for
 * them, at a different weight and a different optical size. These are 16 px
 * stroked paths on a 24 box, they inherit `currentColor`, and they are always
 * `aria-hidden`: an icon-only control carries its own `aria-label`, and an icon
 * beside a word says the same thing the word does.
 */
import type { ReactNode } from 'react';

function Ico({ children, size = 16 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      className="ae-ico"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Flüge — a leg between two points, which is what a flight is here. */
export const IconFlights = (p: { size?: number }) => (
  <Ico {...p}>
    <circle cx="5" cy="18" r="2.2" />
    <circle cx="19" cy="6" r="2.2" />
    <path d="M6.9 16.4 17.2 7.7" />
  </Ico>
);

export const IconAircraft = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M12 3c.9 0 1.4 1 1.4 2.2v3.9l7.1 4.1v2l-7.1-2.1v3.6l2.3 1.7v1.7L12 19.4l-3.7 .7v-1.7l2.3-1.7v-3.6L3.5 15.2v-2l7.1-4.1V5.2C10.6 4 11.1 3 12 3Z" />
  </Ico>
);

export const IconSettings = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
    <circle cx="15" cy="7" r="2.2" />
    <circle cx="9" cy="17" r="2.2" />
  </Ico>
);

export const IconSources = (p: { size?: number }) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5.5" />
    <path d="M12 7.6v.9" />
  </Ico>
);

export const IconMap = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M9 4.5 3.5 6.8v12.7L9 17.2l6 2.3 5.5-2.3V4.5L15 6.8Z" />
    <path d="M9 4.5v12.7M15 6.8v12.7" />
  </Ico>
);

export const IconNavLog = (p: { size?: number }) => (
  <Ico {...p}>
    <rect x="3.5" y="4.5" width="17" height="15" rx="1.5" />
    <path d="M3.5 9.5h17M9.5 9.5v10M3.5 14.5h17" />
  </Ico>
);

export const IconWeather = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M7.5 18h9.2a3.8 3.8 0 0 0 .3-7.6 5.4 5.4 0 0 0-10.3-1A3.8 3.8 0 0 0 7.5 18Z" />
  </Ico>
);

export const IconTerrain = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M2.5 18.5 9 8l4 5.6 2.6-2.8 5.9 7.7Z" />
    <path d="M2.5 5.5h19" />
  </Ico>
);

export const IconForm = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M6 3.5h8.5L19 8v12.5H6Z" />
    <path d="M14 3.5V8h5M9 12h7M9 16h7" />
  </Ico>
);

export const IconExport = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M12 15.5V4M8.2 7.6 12 3.8l3.8 3.8" />
    <path d="M4.5 14.5v4a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-4" />
  </Ico>
);

export const IconMore = (p: { size?: number }) => (
  <Ico {...p}>
    <circle cx="5" cy="12" r="1.4" fill="currentColor" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" />
    <circle cx="19" cy="12" r="1.4" fill="currentColor" />
  </Ico>
);

export const IconAccount = (p: { size?: number }) => (
  <Ico {...p}>
    <circle cx="12" cy="8.5" r="3.5" />
    <path d="M4.8 20a7.4 7.4 0 0 1 14.4 0" />
  </Ico>
);

export const IconBack = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M15 5 8 12l7 7" />
  </Ico>
);

export const IconChevronLeft = IconBack;

export const IconChevronRight = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M9 5l7 7-7 7" />
  </Ico>
);

/** Severity: red. Filled triangle, so it is still a triangle in monochrome. */
export const IconWarning = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M12 3.8 21.2 20H2.8Z" />
    <path d="M12 9.6v4.6M12 17.2v.6" />
  </Ico>
);

/** Severity: amber. */
export const IconCaution = IconWarning;

/** Severity: blue. */
export const IconInfo = (p: { size?: number }) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5.5M12 7.6v.9" />
  </Ico>
);

/**
 * Refresh — the freshness chip's ⟳. It rotates while a source is reading; the
 * old number stays legible beside it, because a value on screen is never
 * blanked to say that a newer one is on its way.
 */
export const IconRefresh = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M20 12a8 8 0 1 1-2.5-5.8" />
    <path d="M20 4.5V9h-4.5" />
  </Ico>
);
